import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, Mail, ArrowUpDown, Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Interface para as notificações do sistema
interface SystemNotification {
  id: string;
  name: string;
  description: string;
  email_template_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  email_template?: EmailTemplate;
}

// Interface para os templates de email
interface EmailTemplate {
  id: string;
  name: string;
}

// Schema de validação para o formulário de notificação
const notificationSchema = z.object({
  name: z.string().min(3, { message: 'O nome deve ter pelo menos 3 caracteres' }),
  description: z.string().min(5, { message: 'A descrição deve ter pelo menos 5 caracteres' }),
  email_template_id: z.string().uuid({ message: 'Selecione um template de email válido' })
});

type NotificationFormValues = z.infer<typeof notificationSchema>;

const SystemNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<SystemNotification[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<SystemNotification | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Formulário para adicionar/editar notificações
  const notificationForm = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      name: '',
      description: '',
      email_template_id: ''
    }
  });

  useEffect(() => {
    fetchNotifications();
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    // Filter notifications based on search query
    if (searchQuery.trim() === '') {
      setFilteredNotifications(notifications);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = notifications.filter(notification => 
        notification.name.toLowerCase().includes(lowercasedQuery) || 
        notification.description.toLowerCase().includes(lowercasedQuery) ||
        (notification.email_template?.name && notification.email_template.name.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredNotifications(filtered);
    }
  }, [searchQuery, notifications]);

  // Buscar notificações do sistema
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_notifications')
        .select(`
          *,
          email_template:email_template_id (
            id,
            name
          )
        `)
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) throw error;
      
      setNotifications(data || []);
      setFilteredNotifications(data || []);
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      toast.error('Erro ao carregar notificações', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar templates de email disponíveis
  const fetchEmailTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      
      setEmailTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching email templates:', error);
      toast.error('Erro ao carregar templates de email', {
        description: error.message
      });
    }
  };

  const handleSort = (column: 'name' | 'created_at' | 'updated_at') => {
    // If clicking the same column, toggle order; otherwise, set new column with asc order
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Re-fetch notifications when sort parameters change
    fetchNotifications();
  }, [sortBy, sortOrder]);

  const openAddDialog = () => {
    notificationForm.reset({
      name: '',
      description: '',
      email_template_id: ''
    });
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (notification: SystemNotification) => {
    setCurrentNotification(notification);
    notificationForm.reset({
      name: notification.name,
      description: notification.description,
      email_template_id: notification.email_template_id
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (notification: SystemNotification) => {
    setCurrentNotification(notification);
    setIsDeleteDialogOpen(true);
  };

  const handleAddNotification = async (values: NotificationFormValues) => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('system_notifications')
        .insert([{
          name: values.name,
          description: values.description,
          email_template_id: values.email_template_id,
          created_by: (await supabase.auth.getUser()).data.user?.email || '',
          updated_by: (await supabase.auth.getUser()).data.user?.email || ''
        }])
        .select();

      if (error) throw error;
      
      toast.success('Notificação adicionada com sucesso');
      setIsAddDialogOpen(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('Error adding notification:', error);
      toast.error('Erro ao adicionar notificação', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditNotification = async (values: NotificationFormValues) => {
    if (!currentNotification) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('system_notifications')
        .update({
          name: values.name,
          description: values.description,
          email_template_id: values.email_template_id,
          updated_by: (await supabase.auth.getUser()).data.user?.email || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', currentNotification.id);

      if (error) throw error;
      
      toast.success('Notificação atualizada com sucesso');
      setIsEditDialogOpen(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('Error updating notification:', error);
      toast.error('Erro ao atualizar notificação', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNotification = async () => {
    if (!currentNotification) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('system_notifications')
        .delete()
        .eq('id', currentNotification.id);

      if (error) throw error;
      
      toast.success('Notificação excluída com sucesso');
      setIsDeleteDialogOpen(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      toast.error('Erro ao excluir notificação', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const getTemplateNameById = (templateId: string) => {
    const template = emailTemplates.find(t => t.id === templateId);
    return template ? template.name : 'Template não encontrado';
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Notificações do Sistema</h1>
          <p className="text-muted-foreground">
            Gerencie as notificações automáticas enviadas pelo sistema.
          </p>
        </div>
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Notificação
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Notificações Configuradas</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar notificações..."
                  className="w-64 pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                    <div className="flex items-center">
                      Nome
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Template de Email</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('created_at')}>
                    <div className="flex items-center">
                      Criado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('updated_at')}>
                    <div className="flex items-center">
                      Atualizado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center p-4">
                      {searchQuery ? 'Nenhuma notificação encontrada para esta busca.' : 'Nenhuma notificação configurada.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNotifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell className="font-extrabold">
                        {notification.name}
                      </TableCell>
                      <TableCell>{notification.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-100">
                          <Mail className="mr-1 h-3 w-3" />
                          {notification.email_template?.name || getTemplateNameById(notification.email_template_id)}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(notification.created_at)}</TableCell>
                      <TableCell>{formatDate(notification.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(notification)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Editar notificação</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openDeleteDialog(notification)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Excluir notificação</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog para adicionar notificação */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Notificação</DialogTitle>
            <DialogDescription>
              Crie uma nova notificação de sistema com um template de email associado.
            </DialogDescription>
          </DialogHeader>
          <Form {...notificationForm}>
            <form onSubmit={notificationForm.handleSubmit(handleAddNotification)} className="space-y-4">
              <FormField
                control={notificationForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome da notificação" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva a finalidade desta notificação" 
                        {...field} 
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="email_template_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template de Email</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um template de email" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {emailTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar notificação */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Notificação</DialogTitle>
            <DialogDescription>
              Atualize os detalhes desta notificação de sistema.
            </DialogDescription>
          </DialogHeader>
          <Form {...notificationForm}>
            <form onSubmit={notificationForm.handleSubmit(handleEditNotification)} className="space-y-4">
              <FormField
                control={notificationForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome da notificação" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva a finalidade desta notificação" 
                        {...field} 
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="email_template_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template de Email</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um template de email" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {emailTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Dialog para confirmar exclusão */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a notificação "{currentNotification?.name}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDeleteNotification}
              disabled={submitting}
            >
              {submitting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemNotifications; 